import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
	type CategoryOption,
	HorizontalCategoryFilter,
} from "@/components/shared/HorizontalCategoryFilter";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { faqCategories, faqItems } from "@/data/faq";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const ITEMS_PER_PAGE = 4;
const MAX_VISIBLE_PAGES = 10;

const FAQSection = () => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeCategory, setActiveCategory] = useState("all");
	const [currentPage, setCurrentPage] = useState(1);
	const isMobile = useIsMobile();

	const filteredFAQs = useMemo(() => {
		return faqItems.filter((item) => {
			const matchesSearch =
				item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
				item.answer.toLowerCase().includes(searchQuery.toLowerCase());
			const matchesCategory =
				activeCategory === "all" || item.categories.includes(activeCategory);
			return matchesSearch && matchesCategory;
		});
	}, [searchQuery, activeCategory]);

	// Count questions per category
	const categoryCountsMap = useMemo(() => {
		const counts: Record<string, number> = { all: faqItems.length };
		faqItems.forEach((item) => {
			item.categories.forEach((category) => {
				counts[category] = (counts[category] || 0) + 1;
			});
		});
		return counts;
	}, []);

	// Transform categories for the reusable component
	const categoryOptions: CategoryOption[] = useMemo(() => {
		return faqCategories.map((cat) => ({
			id: cat.id,
			label: cat.label,
			count: categoryCountsMap[cat.id] || 0,
		}));
	}, [categoryCountsMap]);

	const totalPages = Math.ceil(filteredFAQs.length / ITEMS_PER_PAGE);

	// Calculate visible pages for pagination
	const getVisiblePages = () => {
		const maxVisible = isMobile ? 3 : MAX_VISIBLE_PAGES;
		if (totalPages <= maxVisible) {
			return Array.from({ length: totalPages }, (_, i) => i + 1);
		}

		const halfVisible = Math.floor(maxVisible / 2);
		let startPage = Math.max(1, currentPage - halfVisible);
		const endPage = Math.min(totalPages, startPage + maxVisible - 1);

		if (endPage - startPage + 1 < maxVisible) {
			startPage = Math.max(1, endPage - maxVisible + 1);
		}

		return Array.from(
			{ length: endPage - startPage + 1 },
			(_, i) => startPage + i,
		);
	};

	const paginatedFAQs = useMemo(() => {
		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
		return filteredFAQs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
	}, [filteredFAQs, currentPage]);

	// Reset to page 1 when filters change
	const handleCategoryChange = (category: string) => {
		setActiveCategory(category);
		setCurrentPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearchQuery(value);
		setCurrentPage(1);
	};

	return (
		<section id="faq" className="py-16">
			<div className="text-center mb-12 animate-slide-up">
				<h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
					Perguntas Frequentes
				</h2>
				<p className="text-muted-foreground text-lg max-w-2xl mx-auto">
					Tire suas dúvidas sobre o Cadastro Imobiliário Brasileiro
				</p>
			</div>

			{/* Search and Filter */}
			<div className="max-w-3xl mx-auto mb-8 space-y-4 animate-slide-up">
				<div className="relative">
					<Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
					<Input
						type="text"
						placeholder="Buscar pergunta..."
						value={searchQuery}
						onChange={(e) => handleSearchChange(e.target.value)}
						className="pl-12 py-6 rounded-full border-border bg-card"
					/>
				</div>

				{/* Reusable Category Filter */}
				<HorizontalCategoryFilter
					categories={categoryOptions}
					activeCategory={activeCategory}
					onCategoryChange={handleCategoryChange}
				/>
			</div>

			{/* FAQ Accordion */}
			<div className="max-w-3xl mx-auto">
				{paginatedFAQs.length > 0 ? (
					<>
						<Accordion type="single" collapsible className="space-y-3">
							{paginatedFAQs.map((item, index) => (
								<AccordionItem
									key={item.id}
									value={item.id}
									className={cn(
										"bg-card rounded-2xl px-6 border-none shadow-xs animate-slide-up",
										`stagger-${Math.min(index + 1, 6)}`,
									)}
								>
									<AccordionTrigger className="text-left font-medium hover:no-underline py-5">
										{item.question}
									</AccordionTrigger>
									<AccordionContent className="text-foreground/90 pb-5 leading-relaxed text-base">
										{item.answer}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="flex items-center justify-center gap-2 mt-8">
								<button
									type="button"
									onClick={() =>
										setCurrentPage((prev) => Math.max(prev - 1, 1))
									}
									disabled={currentPage === 1}
									className="p-2 rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
									aria-label="Página anterior"
								>
									<ChevronLeft className="h-5 w-5" />
								</button>

								<div className="flex items-center gap-1">
									{getVisiblePages().map((page) => (
										<button
											type="button"
											key={page}
											onClick={() => setCurrentPage(page)}
											className={cn(
												"w-10 h-10 rounded-full text-sm font-medium transition-all",
												currentPage === page
													? "bg-primary text-primary-foreground"
													: "bg-muted text-muted-foreground hover:bg-muted/80",
											)}
										>
											{page}
										</button>
									))}
								</div>

								<button
									type="button"
									onClick={() =>
										setCurrentPage((prev) => Math.min(prev + 1, totalPages))
									}
									disabled={currentPage === totalPages}
									className="p-2 rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
									aria-label="Próxima página"
								>
									<ChevronRight className="h-5 w-5" />
								</button>
							</div>
						)}
					</>
				) : (
					<div className="text-center py-12 text-muted-foreground">
						<p>Nenhuma pergunta encontrada para sua busca.</p>
					</div>
				)}
			</div>
		</section>
	);
};

export default FAQSection;
